/**
 *
 * @module model/master
 * @author Adam Pirani [greg.babula@mlb.com]
 * @description MLB Scores master model
 * @note inherits methods and properties from the g5-component model
 *
 */

import isEqual from 'lodash-es/isEqual';
import g5Model from 'g5-component/src/scripts/model/master';
import bamHydrates from 'bam-hydrates/index';

/**
 * @class MasterModel
 * Logic for
 */
class MasterModel extends g5Model {

    /**
     *
     * @constructor MasterModel
     * @param {Object} opts shared options Object
     *
     */
    constructor(opts) {

        super(opts);

        let defaultOps = {
            gamePk: '',
            interval: 15000,
            enableFetch: true,
            enablePolling: true,
            useHttps: false,
            mode: 'responsive', // This must match in component/master::addClass()
            sportId: 1,
            path: 'http://statsapi.mlb.com/api/v1/schedule',
            siteAssetsUri: '/node_modules/mlb-site-core/site-core',
            siteImageUri: 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets',
            autoPlay: false,
            showDescription: 'N',
            defaultPromo: {},
            isPostseasonPush: false,
            lang: 'en',
            liveLookInSponsor: '',
            imgContext: 'sport',
            hydratesBase: {
                team: {},
                linescore: {},
                flags: {},
                liveLookin: {},
                review: {}
            },
            hydratesMediawall: {
                person: {},
                stats: {},
                probablePitcher: {}
            },
            hydratesResponsiveAndMIG: {
                broadcasts: {
                    all: {}
                },
                decisions: {},
                linescore: {
                    matchup: {},
                    runners: {}
                },
                person: {},
                probablePitcher: {},
                stats: {}
            },
            hydratesButtons: {
                game: {
                    content: {
                        summary: {},
                        media: {
                            epg: {}
                        }
                    },
                    tickets: {}
                },
                xrefId: {}
            },
            hydratesResponsiveOnly: {
                homeRuns: {},
                previousPlay: {},
                team: {
                    leaders: {
                        showOnPreview: {
                            'leaderCategories=[homeRuns,runsBattedIn,battingAverage]': {},
                            'statGroup=[pitching,hitting]': {}
                        }
                    }
                },
                game: {
                    content: {
                        media: {
                            featured: {}
                        }
                    }
                }
            },
            hydratesSeriesStatus: {
                seriesStatus: {
                    'useOverride=true': {}
                }
            },
            hydrateBroadcasts: {
                broadcasts: {
                    all: {}
                }
            },
            hydratesPromotions: {
                game: {
                    promotions: {}
                }
            },
            hydratesCurrentTeam: {
                person: {
                    currentTeam: {}
                }
            }
        };

        this.opts = Object.assign(defaultOps, opts);

    }

    /**
     *
     * @method fetch
     * @description makes a GET request to specified path, emits data event, expecting JSON by default
     * @returns {Object} this
     *
     */
    fetch() {

        let { opts } = this;
        let { sportId, gamePk, path, hydratesBase, enablePolling, interval, mode, lang, date, hydratesButtons } = opts;
        let { hydratesMediawall, hydratesResponsiveAndMIG, hydratesResponsiveOnly, hydratesSeriesStatus } = opts;
        let { hydrateBroadcasts, hydratesPromotions, hydratesCurrentTeam, milbSportIds = '' } = opts;

        let fullHydrateObj = hydratesBase;

        if (mode === 'responsive') {

            fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesResponsiveOnly, hydratesButtons, hydratesSeriesStatus);

        } else if (mode === 'MIG') {

            fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesResponsiveAndMIG, hydratesButtons, hydratesSeriesStatus);

            let sId = parseInt(sportId, 10);
            if (milbSportIds.indexOf(sId) !== -1) {
                hydratesCurrentTeam.person['appContext=minorLeague'] = {};
                fullHydrateObj = bamHydrates.merge(fullHydrateObj, hydratesPromotions, hydratesCurrentTeam);
            }

        } else if (mode === 'mediawall') {

            fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesMediawall, hydratesButtons);

        } else if (mode === 'minisb') {

            fullHydrateObj = bamHydrates.merge(hydratesBase, hydratesButtons, hydratesSeriesStatus, hydrateBroadcasts);

        }

        let hydrateStr = bamHydrates.stringify(fullHydrateObj);

        //
        // constructs dataPath if gamePk is present
        // otherwise, attempts to simply hit the path provided
        // path is the PROD stats api by default, however it can also be
        // set to the dev/qa stats api or a local file path
        //
        let optsPath = !opts.useHttps ? path : path.replace('http:', 'https:');

        let dataPath = gamePk ? `${optsPath}
            ?sportId=${sportId}
            &gamePk=${gamePk}
            &hydrate=${hydrateStr}
            &useLatestGames=true
            &language=${lang}
            ${date ? `&flipDate=${date}` : ''}
        `.replace(/\s/g, '') : optsPath;

        /**
         *
         * @function handleData
         * @param {Object} response
         * @returns {Object} response JSON
         * @description handles response and returns JSON if successful
         *
         */
        const handleData = (response) => {

            return JSON.parse(response);

        };

        /**
         *
         * @function handleSuccess
         * @param {Object} data parsed JSON
         *
         */
        const handleSuccess = (data = {}) => {

            if (!isEqual(data, this.dataCache)) {

                this.dataCache = data;
                this.emit('data', data);

            }

        };

        /**
         *
         * @function handleError
         * @param {Number|Object} err
         *
         */
        const handleError = (err) => {

            this.emit('data-error', err);

        };

        this.xhr(dataPath, handleError, handleData, handleSuccess);

        this.dataFetch = enablePolling && setTimeout(this.fetch.bind(this), interval);

        return this;

    }

}

export default MasterModel;
